import { useMemo, useState, type CSSProperties } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { useNavigate } from 'react-router-dom'
import {
  callNumber,
  changeQty,
  db,
  markDone,
  markReady,
  openOrderForTable,
  saveItems,
  voidOrder,
  type DiningTable,
  type Order,
  type Zone,
} from '../lib/db'
import {
  clock,
  dayText,
  elapsedMinutes,
  elapsedText,
  money,
  qtyToText,
  whenText,
  QTY_UNIT,
} from '../lib/money'
import { useMediaQuery } from '../lib/hooks'
import { useApp } from '../lib/store'
import { PageHead } from './PageHead'
import { ClockMark, CupMark, PencilMark, PersonMark } from './StateIcons'
import { TableGlyph, TableIcon } from './TableIcon'
import { GRID_COLS, GRID_GAP, MIN_ROWS } from './FloorPlan'
import { NoteSheet } from './NoteSheet'
import { Sheet } from './Sheet'
import { Thumb } from './Thumb'

/** Chiều cao một hàng của sơ đồ tự xếp. Đủ cho ô bàn bản rút gọn, không hơn. */
const FLOOR_ROW = 152

/*
 * Sơ đồ tự xếp dựng bằng phần trăm, không đo bề ngang nữa.
 *
 * Bản trước đo khung bằng JavaScript rồi đặt ô bàn theo pixel. Đo hụt một nhịp — lúc khoang
 * đơn bên phải chưa kịp chiếm chỗ chẳng hạn — là cả sơ đồ rộng hơn khung và mọc ra một
 * thanh cuộn ngang. Cột tính bằng phần trăm thì không có nhịp nào để hụt: rộng bao nhiêu
 * cũng vừa đúng khung, khỏi thanh cuộn.
 *
 * Hàng vẫn tính bằng pixel vì chiều cao không có gì để chia. Ô lưới thành hơi chữ nhật thay
 * vì vuông chằn chặn, nhưng thứ chủ quán cần đọc là bàn nào cạnh bàn nào, và cái đó không
 * đổi.
 */
const colStart = (n: number) =>
  `calc((100% - ${(GRID_COLS - 1) * GRID_GAP}px) / ${GRID_COLS} * ${n} + ${n * GRID_GAP}px)`

const colSpan = (n: number) =>
  `calc((100% - ${(GRID_COLS - 1) * GRID_GAP}px) / ${GRID_COLS} * ${n} + ${(n - 1) * GRID_GAP}px)`

/**
 * Màn hình bán tại bàn của bộ Modern.
 *
 * Hai khoang: sơ đồ bàn bên trái, đơn của bàn đang chọn bên phải. Máy quầy nằm ngang có
 * thừa chỗ cho cả hai, và khi có cả hai thì người bán trả lời được câu hỏi hay gặp nhất
 * — "bàn 3 đang ăn những gì, hết bao nhiêu rồi" — mà không phải rời khỏi sơ đồ.
 *
 * Màn hình hẹp không đủ chỗ cho khoang bên phải, nên ở đó chạm vào bàn là đi thẳng sang
 * màn hình gọi món như cũ. Một chạm, không thêm bước nào.
 */

/** Bàn ngồi quá lâu thì đổi sang cảnh báo — quán đông, cần biết bàn nào sắp nên nhắc. */
const LONG_SIT_MINUTES = 90

const ALL = 'all'

/**
 * Che số điện thoại, chỉ chừa bốn số cuối.
 *
 * Sơ đồ bàn là màn hình mở suốt ngày ở quầy, khách đứng trả tiền cũng nhìn thấy. Bốn số
 * cuối đủ để người bán đối chiếu khi khách gọi tới, mà không phơi cả số ra cho người lạ.
 */
function maskPhone(phone: string): string {
  const digits = phone.replace(/\D/g, '')
  return digits.length <= 4 ? digits : `••• ${digits.slice(-4)}`
}

type TableState = 'free' | 'idle' | 'busy' | 'long' | 'serving' | 'ready' | 'reserved'

/** "3 món" hoặc "3 món · 5 phần" khi có dòng gọi nhiều hơn một phần. */
function countOf(order: Order): string {
  const pieces = order.items.reduce((sum, i) => sum + i.qtyMilli, 0)
  const lines = order.items.length
  return pieces > lines * QTY_UNIT ? `${lines} món · ${qtyToText(pieces)} phần` : `${lines} món`
}

/** Bàn đã đặt và giờ hẹn chưa trôi qua quá nửa tiếng — vẫn phải giữ chỗ. */
const isBooked = (table: DiningTable, now: number): boolean =>
  !!table.reservedAt && new Date(table.reservedAt).getTime() > now - 30 * 60_000

function stateOf(table: DiningTable, order: Order | undefined, now: number): TableState {
  /*
   * Đã thu tiền nhưng chưa xong việc.
   *
   * Quán bây giờ phần lớn thu tiền ngay lúc gọi món: khách vào, gọi, trả tiền, rồi mới
   * ngồi xuống. Nên trả tiền xong là lúc bàn <b>bắt đầu</b> bận, không phải lúc nó trống.
   * Ô bàn chỉ trắng lại khi nhân viên bấm dọn bàn hoặc giao đồ tận tay khách.
   */
  if (order && order.status === 'Paid' && !order.doneAt) {
    return order.readyAt ? 'ready' : 'serving'
  }

  if (order && order.status === 'Open') {
    /*
     * Đơn chưa có món nào là một trạng thái riêng, không phải "đang phục vụ".
     *
     * Nó nghĩa là bàn đã có người ngồi nhưng chưa gọi gì — hoặc ai đó lỡ mở bàn ra rồi
     * bỏ đó. Gộp vào "đang phục vụ" thì người bán nhìn sơ đồ thấy bàn đang bận, chạm vào
     * lại thấy trống trơn, và lần sau họ thôi tin cái sơ đồ.
     *
     * Cũng vì vậy mà "ngồi lâu" chỉ đếm khi đã gọi món: mấy cái đơn rỗng mở từ sáng đến
     * chiều mà đeo nhãn đỏ thì lúc có bàn ngồi lâu thật cũng chẳng ai nhìn nữa.
     */
    if (order.items.length === 0) return 'idle'
    return elapsedMinutes(order.openedAt, now) >= LONG_SIT_MINUTES ? 'long' : 'busy'
  }
  if (isBooked(table, now)) return 'reserved'
  return 'free'
}

/*
 * Nhãn trạng thái: một cái huy hiệu tròn, chỉ có hình, không có chữ.
 *
 * Chữ 11px trên góc ô bàn là thứ không ai đọc — đứng bán cách máy một sải tay thì nó là
 * một vệt mờ, mà bốn năm ô đều đeo một vệt mờ như nhau thì sơ đồ trông rối hơn là rõ.
 * Hình thì bắt được từ xa, và màu nền của huy hiệu đã nói phần còn lại.
 *
 * Chữ vẫn còn, nằm trong `title` và trong lớp ẩn cho trình đọc màn hình: rê chuột vào là
 * hiện, người khiếm thị vẫn nghe được. Chỉ là nó không chiếm chỗ trên mặt kính nữa.
 *
 * Bàn trống thì không đeo gì cả. Ô trắng trơn, không tiền, không nhãn — bản thân sự trống
 * trải ấy đã là câu trả lời, thêm một cái huy hiệu nữa chỉ tổ làm sơ đồ đầy lên.
 */
const STATE_TEXT: Record<TableState, string> = {
  free: 'Trống',
  idle: 'Chưa gọi món',
  busy: 'Đang phục vụ',
  long: 'Ngồi lâu',
  serving: 'Khách còn ngồi',
  ready: 'Đã lên món',
  reserved: 'Đã đặt trước',
}

function StateBadge({ state }: { state: TableState }) {
  if (state === 'free') return null

  return (
    <span className="tile-badge" title={STATE_TEXT[state]}>
      {state === 'idle' && <PencilMark />}
      {(state === 'busy' || state === 'serving') && <PersonMark />}
      {(state === 'long' || state === 'reserved') && <ClockMark />}
      {state === 'ready' && <CupMark />}
      <span className="sr-only">{STATE_TEXT[state]}</span>
    </span>
  )
}

interface TileProps {
  table: DiningTable
  order: Order | undefined
  /** Ô này đang giữ mấy đơn. Bàn vừa thu tiền mà khách còn ngồi thì có thể có hai. */
  queued: number
  now: number
  selected: boolean
  onClick: () => void
  /** Vị trí tuyệt đối khi đang vẽ theo sơ đồ tự xếp. */
  style?: CSSProperties
  /** Bản rút gọn cho ô nhỏ: bỏ số chỗ ngồi, thu nhỏ hình tròn và cỡ chữ. */
  compact?: boolean
  /** Đang lọc và ô này không thuộc nhóm đang xem: mờ đi, bấm không ăn. */
  dim?: boolean
}

function TableTile({
  table,
  order,
  queued,
  now,
  selected,
  onClick,
  style,
  compact,
  dim,
}: TileProps) {
  const state = stateOf(table, order, now)
  const busy = state === 'busy' || state === 'long'

  return (
    <button
      type="button"
      className={`tile-table is-${state}${selected ? ' is-picked' : ''}${compact ? ' is-small' : ''}${dim ? ' is-dim' : ''}`}
      style={style}
      disabled={dim}
      onClick={onClick}
      aria-pressed={selected}
      aria-label={
        busy && order
          ? `${table.name}, đang có khách, ${money(order.total)} đồng, ngồi ${elapsedText(order.openedAt, now)}`
          : state === 'idle' && order
            ? `${table.name}, có người ngồi nhưng chưa gọi món, ${elapsedText(order.openedAt, now)}`
            : state === 'reserved'
              ? `${table.name}, đã đặt trước ${whenText(table.reservedAt!, new Date(now))}`
              : `${table.name}, trống`
      }
      title={
        isBooked(table, now)
          ? `Đã đặt ${whenText(table.reservedAt!, new Date(now))}${table.reservedName ? ` · ${table.reservedName}` : ''}`
          : undefined
      }
    >
      <StateBadge state={state} />

      {/*
        Đồng hồ đặt bàn hiện cả khi bàn đang có khách.
        Trước đây trạng thái "đang phục vụ" nuốt mất cái hẹn: bàn có khách ngồi mà 19h30
        tối nay đã có người đặt thì người bán không thấy gì, tới giờ mới biết là kẹt.
      */}
      {state !== 'reserved' && isBooked(table, now) && (
        <span className="tile-book" title={`Đã đặt ${whenText(table.reservedAt!, new Date(now))}`}>
          <span aria-hidden="true">⏰</span>
          {clock(table.reservedAt!)}
        </span>
      )}

      {/* Còn mấy đơn nữa đang treo ở ô này. Không có con số thì bốn người kia vô hình. */}
      {queued > 1 && (
        <span className="tile-queue" title={`${queued} đơn đang chờ`}>
          {queued} đơn
        </span>
      )}

      <TableIcon icon={table.icon} className="tile-disc" />

      <b className="tile-table-name">{table.name}</b>
      <small className="tile-seats">{table.seats > 0 ? `${table.seats} người` : ''}</small>

      {selected && (
        <span className="tile-pick" aria-hidden="true">
          <svg viewBox="0 0 24 24" focusable="false">
            <path
              d="M5 12.5 10 17.5 19 7.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      )}

      {(state === 'serving' || state === 'ready') && order ? (
        <span className="tile-money num">
          {money(order.total)}
          <em>{order.customerName ?? `Số ${callNumber(order.code)}`}</em>
        </span>
      ) : busy && order ? (
        <span className="tile-money num">
          {money(order.total)}
          <em>{elapsedText(order.openedAt, now)}</em>
        </span>
      ) : state === 'idle' && order ? (
        /* Số 0 đồng không nói thêm được gì — thứ đáng biết là bàn đã ngồi bao lâu mà
           chưa gọi, để còn ra hỏi khách. */
        <span className="tile-idle">Ngồi {elapsedText(order.openedAt, now)}</span>
      ) : state === 'reserved' ? (
        <span className="tile-when">{whenText(table.reservedAt!, new Date(now))}</span>
      ) : null}
    </button>
  )
}

interface Props {
  zones: Zone[]
  tables: DiningTable[]
  ordersByTable: Map<string, Order[]>
  now: number
  owner: boolean
  /** Quán đang dùng sơ đồ tự xếp: vẽ ô bàn đúng chỗ chủ quán đã kéo. */
  floor: boolean
  /** Cỡ ô bàn, phần trăm. Chủ quán kéo cho vừa cái máy bảng của quán. */
  scale: number
  onScale: (percent: number) => void
  onArrange: () => void
  onEditTable: (t: DiningTable) => void
  onReserve: (t: DiningTable) => void
}

export function TableBoard({
  zones,
  tables,
  ordersByTable,
  now,
  owner,
  floor,
  scale,
  onScale,
  onArrange,
  onEditTable,
  onReserve,
}: Props) {
  const nav = useNavigate()
  const say = useApp((x) => x.say)

  // Cùng một mốc với câu @media đang ẩn khoang bên phải. Hai chỗ phải khớp nhau, không
  // thì có màn hình chạm vào bàn mà chẳng thấy gì xảy ra.
  const hasPane = useMediaQuery('(min-width: 1180px)')

  /*
   * Danh mục món, chỉ để tra ra ảnh.
   *
   * Dòng trong đơn chụp lại tên và giá lúc bán chứ không giữ mã ảnh — cố ý, vì đổi ảnh
   * món sau này không được làm sai một hoá đơn đã in. Muốn hiện ảnh thì phải tra ngược
   * ra sản phẩm, và món đã bị xoá khỏi danh mục thì rơi về chữ cái đầu.
   */
  const products = useLiveQuery(() => db.products.toArray(), [], [])
  const imageOf = (productId: string | null) =>
    productId ? (products.find((p) => p.id === productId)?.imageKey ?? null) : null

  const [zone, setZone] = useState<string>(ALL)
  const [query, setQuery] = useState('')
  const [pickedId, setPickedId] = useState<string | null>(null)
  const [pickedOrderId, setPickedOrderId] = useState<string | null>(null)
  const [noting, setNoting] = useState<Order['items'][number] | null>(null)
  /*
   * Hỏi lại trước khi dọn bàn.
   *
   * Dọn bàn là cái nút một chiều: bấm xong thì ô bàn trắng lại, đơn rời khỏi sơ đồ và
   * không có đường nào kéo nó về. Mà nó lại nằm ngay cạnh "Chưa xong" — hai nút sát nhau,
   * một cái vô hại, một cái không lùi được, và tay người bán thì đang vừa bưng vừa bấm.
   */
  const [clearing, setClearing] = useState(false)
  const [searching, setSearching] = useState(false)
  /*
   * Chỉ xem một nhóm bàn.
   *
   * Câu hỏi hay gặp nhất ở cửa quán là "còn bàn nào trống không", và câu trả lời đang nằm
   * rải rác giữa hai ba chục ô. Bấm một cái cho mấy ô kia mờ đi thì mắt không phải lọc nữa
   * — chỗ nào còn sáng là chỗ xếp được khách.
   */
  const [only, setOnly] = useState<'busy' | 'free' | null>(null)

  /*
   * Cỡ ô bàn đổi ngay dưới ngón tay, còn việc gửi lên máy chủ thì đợi thả tay ra.
   *
   * Kéo một đường là hàng chục nhịp; gửi mỗi nhịp một lần thì mạng 3G ở quán tắc ngay, mà
   * ô bàn cũng giật theo từng lần trả lời. Giữ một bản tại chỗ để vẽ, thả tay mới chốt.
   */
  const [draft, setDraft] = useState<number | null>(null)
  const k = (draft ?? scale) / 100

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase()
    return tables.filter(
      (t) => (zone === ALL || t.zoneId === zone) && (!q || t.name.toLowerCase().includes(q)),
    )
  }, [tables, zone, query])

  // Bàn đang chọn có thể vừa bị lọc mất khỏi danh sách, hoặc vừa bị xoá ở máy khác.
  const picked = shown.find((t) => t.id === pickedId) ?? null

  /*
   * Hàng đợi của ô đang chọn, và đơn đang mở trong khoang bên phải.
   *
   * Mặc định là đơn đầu hàng — đơn cần xử lý trước. Bấm vào một dòng khác trong hàng thì
   * khoang đổi sang đơn ấy, để nhân viên gọi đúng người khi khách tới lấy không theo thứ tự.
   */
  const queue = picked ? (ordersByTable.get(picked.id) ?? []) : []
  const order = queue.find((o) => o.id === pickedOrderId) ?? queue[0]

  const busyCount = shown.filter((t) => ordersByTable.get(t.id)).length
  const freeCount = shown.length - busyCount
  const dimmed = (t: DiningTable) =>
    only !== null && (only === 'busy') !== Boolean(ordersByTable.get(t.id))

  /** Chiều cao sơ đồ đo theo ô bàn nằm thấp nhất, không dưới bốn hàng cho đỡ trống trải. */
  const floorRows = Math.max(MIN_ROWS, ...shown.map((t) => t.gridY + t.gridH), MIN_ROWS)

  async function openOrder(table: DiningTable) {
    const o = await openOrderForTable(table)
    nav(`/order/${o.id}`)
  }

  async function bump(item: Order['items'][number], delta: number) {
    if (!order) return
    await saveItems(order.id, changeQty(order.items, item.id, delta * QTY_UNIT))
  }

  /**
   * Trả một cái bàn về trống.
   *
   * Nhân viên chạm nhầm vào bàn trống là mở ngay một đơn, quay ra thì bàn đã đeo nhãn có
   * khách — và trước đây không có đường nào gỡ nhãn ấy ngoài việc gọi một món rồi huỷ cả
   * đơn. Chỉ cho gỡ khi đơn <b>chưa có món nào</b>: đơn đã gọi món thì huỷ là chuyện có
   * dính tiền, phải làm ở màn hình gọi món nơi người bán nhìn thấy mình đang xoá cái gì.
   */
  /** Khách đứng dậy rồi. Tới đây ô bàn mới trắng lại. */
  async function finish(order: Order, table: DiningTable) {
    await markDone(order.id)
    setClearing(false)
    say(`Đã dọn ${table.name}`)
  }

  /** Chạm vào một ô bàn. Màn hẹp không có khoang bên phải thì đi thẳng vào gọi món. */
  function pickTile(t: DiningTable) {
    if (!hasPane) {
      void openOrder(t)
      return
    }
    setPickedId(t.id)
    setPickedOrderId(null)
    setClearing(false)
  }

  async function clearTable(table: DiningTable, open: Order) {
    await voidOrder(open.id)
    say(`Đã trả ${table.name} về trống`)
  }

  async function drop(itemId: string) {
    if (!order) return
    await saveItems(
      order.id,
      order.items.filter((i) => i.id !== itemId),
    )
  }

  return (
    <div className="board tables-board" style={{ '--tile-k': k } as CSSProperties}>
      <PageHead
        to="/"
        icon={<TableGlyph />}
        title="Bán tại bàn"
        sub="Chọn bàn để bắt đầu gọi món"
      />

      <div className="tables-split">
        <div className="tables-main">
          <div className="zone-row">
            <div className="chip-row">
              <button
                type="button"
                className="chip"
                aria-pressed={zone === ALL}
                onClick={() => setZone(ALL)}
              >
                Tất cả
              </button>
              {zones.map((z) => (
                <button
                  key={z.id}
                  type="button"
                  className="chip"
                  aria-pressed={zone === z.id}
                  onClick={() => setZone(z.id)}
                >
                  {z.name}
                </button>
              ))}
            </div>

            {/*
              Hai con số, mà cũng là hai cái nút.
              Trước đây chỗ này là dòng chữ "2/13 bàn có khách" — đọc xong vẫn phải tự đi
              tìm xem bàn nào. Giờ bấm vào là mấy bàn không thuộc nhóm ấy mờ đi và khoá
              lại, nên câu trả lời hiện ngay trên sơ đồ chứ không nằm trong đầu người đọc.

              Chữ "có khách" và "còn trống" nhường chỗ cho hình: hình người là có người
              ngồi, cái bàn trơ trọi là chưa ai ngồi — đúng hai hình sơ đồ đang dùng. Quán
              có bốn năm khu vực thì dải chip khu vực mới là thứ cần chỗ ở hàng này.
            */}
            <div className="tally-row">
              <button
                type="button"
                className="tally is-busy"
                aria-pressed={only === 'busy'}
                title="Chỉ xem bàn đang có khách"
                onClick={() => setOnly((v) => (v === 'busy' ? null : 'busy'))}
              >
                <b className="num">
                  {busyCount}/{shown.length}
                </b>
                <PersonMark />
                <span className="sr-only">bàn có khách</span>
              </button>
              <button
                type="button"
                className="tally is-free"
                aria-pressed={only === 'free'}
                title="Chỉ xem bàn còn trống"
                onClick={() => setOnly((v) => (v === 'free' ? null : 'free'))}
              >
                <b className="num">
                  {freeCount}/{shown.length}
                </b>
                <TableGlyph className="state-glyph" />
                <span className="sr-only">bàn còn trống</span>
              </button>
            </div>

            {/* Ô tìm thu về một cái kính lúp. Quán mười lăm bàn thì tìm bằng mắt nhanh hơn
                gõ, nên cái ô rộng ấy chiếm chỗ cả ngày để dùng vài lần một tuần. */}
            <button
              type="button"
              className="icon-btn"
              aria-expanded={searching}
              aria-label="Tìm bàn"
              title="Tìm bàn"
              onClick={() => {
                if (searching) setQuery('')
                setSearching((v) => !v)
              }}
            >
              <svg viewBox="0 0 24 24" className="glyph" aria-hidden="true" focusable="false">
                <path
                  d={
                    searching
                      ? 'M6.5 6.5l11 11M17.5 6.5l-11 11'
                      : 'M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14zM20 20l-4.2-4.2'
                  }
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            {/*
              Thanh kéo cỡ ô bàn.
              Quán chạy trên đủ loại máy bảng, không có cỡ nào đúng cho mọi màn hình — máy
              8 inch thì tám cái bàn đã chật, máy 13 inch thì thừa chỗ mà ô vẫn bé. Để chủ
              quán tự kéo cho vừa, rồi lưu lên máy chủ để đổi máy vẫn còn.
            */}
            {owner && (
              <label className="tile-zoom" title="Cỡ ô bàn">
                <span className="sr-only">Cỡ ô bàn</span>
                <span className="tile-zoom-mark is-sm" aria-hidden="true" />
                <input
                  type="range"
                  min={60}
                  max={160}
                  step={5}
                  value={draft ?? scale}
                  onChange={(e) => setDraft(Number(e.target.value))}
                  onPointerUp={() => {
                    if (draft !== null && draft !== scale) onScale(draft)
                    setDraft(null)
                  }}
                  onKeyUp={() => {
                    if (draft !== null && draft !== scale) onScale(draft)
                    setDraft(null)
                  }}
                />
                <span className="tile-zoom-mark is-lg" aria-hidden="true" />
              </label>
            )}

            {owner && (
              <button
                type="button"
                className="icon-btn"
                onClick={onArrange}
                aria-label="Sắp xếp bàn"
                title="Sắp xếp bàn"
              >
                ✥
              </button>
            )}
          </div>

          {searching && (
            <div className="menu-search">
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Tìm bàn…"
                inputMode="search"
                autoFocus
              />
            </div>
          )}

          {shown.length === 0 ? (
            <div className="empty">
              <TableIcon className="empty-icon" />
              <p>{query ? `Không có bàn nào tên "${query}".` : 'Khu vực này chưa có bàn nào.'}</p>
            </div>
          ) : floor ? (
            /*
              Sơ đồ tự xếp, y như lúc chủ quán kéo.
              Trước đây toạ độ ấy chỉ hiện trong màn hình sắp xếp rồi thôi — người bán quay
              ra vẫn thấy một lưới đều, nên công sức dựng lại mặt bằng thật đổ sông đổ bể.
              Cả hai nơi giờ đọc chung một bộ toạ độ và chung một cỡ lưới.
            */
            <div
              className="tile-floor"
              style={{ height: floorRows * FLOOR_ROW * k + (floorRows - 1) * GRID_GAP }}
            >
              {shown.map((t) => (
                <TableTile
                  key={t.id}
                  table={t}
                  order={ordersByTable.get(t.id)?.[0]}
                  queued={ordersByTable.get(t.id)?.length ?? 0}
                  now={now}
                  selected={hasPane && t.id === pickedId}
                  compact
                  dim={dimmed(t)}
                  style={{
                    left: colStart(t.gridX),
                    width: colSpan(t.gridW),
                    top: t.gridY * (FLOOR_ROW * k + GRID_GAP),
                    height: t.gridH * FLOOR_ROW * k + (t.gridH - 1) * GRID_GAP,
                  }}
                  onClick={() => pickTile(t)}
                />
              ))}
            </div>
          ) : (
            <div className="tile-tables">
              {shown.map((t) => (
                <TableTile
                  key={t.id}
                  table={t}
                  order={ordersByTable.get(t.id)?.[0]}
                  queued={ordersByTable.get(t.id)?.length ?? 0}
                  now={now}
                  selected={hasPane && t.id === pickedId}
                  dim={dimmed(t)}
                  onClick={() => pickTile(t)}
                />
              ))}
            </div>
          )}
        </div>

        <aside className="order-pane" aria-label="Đơn của bàn đang chọn">
          {!picked ? (
            <div className="pane-empty">
              <div className="empty-icon" aria-hidden="true">
                👆
              </div>
              <p>Chạm vào một bàn để xem đơn và thu tiền.</p>
            </div>
          ) : (
            <>
              <div className="pane-head">
                <span className="pane-name">{picked.name}</span>
                {/* Đã gọi mấy món, mấy phần. Bốn ly cùng một món nằm chung một dòng, nên
                    số dòng một mình không đủ để người bưng bê đếm khay. */}
                {order && order.items.length > 0 && (
                  <b className="pane-count">{countOf(order)}</b>
                )}
                {/* Tên khách và số gọi — quầy mang về đông người đứng đợi cần đúng hai
                    thứ này khi đồ làm xong. */}
                {order && (
                  <span className="pane-call num" title={`Số gọi ${callNumber(order.code)}`}>
                    {callNumber(order.code)}
                  </span>
                )}
                {order?.customerName && <span className="pane-who">{order.customerName}</span>}
                <span className="pane-seats">{picked.seats > 0 ? `${picked.seats} người` : ''}</span>
                <button
                  type="button"
                  className="icon-btn"
                  onClick={() => onEditTable(picked)}
                  aria-label={`Sửa ${picked.name}`}
                  title="Sửa bàn"
                >
                  ✎
                </button>
              </div>

              {/*
                Một bàn thường chỉ có một đơn. Hai đơn xảy ra khi khách đã trả tiền mà còn
                ngồi, rồi gọi thêm — lúc đó phải chọn được đơn nào đang xử lý.
              */}
              {queue.length > 1 && (
                <div className="pane-queue" role="tablist" aria-label="Đơn đang chờ">
                  {queue.map((o) => (
                    <button
                      key={o.id}
                      type="button"
                      role="tab"
                      aria-selected={o.id === order?.id}
                      className={`queue-chip${o.readyAt && !o.doneAt ? ' is-ready' : ''}`}
                      onClick={() => setPickedOrderId(o.id)}
                    >
                      <span className="num">{callNumber(o.code)}</span>
                      <b>{o.customerName ?? (o.status === 'Open' ? 'Đang gọi' : 'Chờ lấy')}</b>
                      <small className="num">{money(o.total)}</small>
                    </button>
                  ))}
                </div>
              )}

              {order && order.items.length > 0 ? (
                <>
                  <div className="pane-lines">
                    {order.items.map((i) => (
                      <div key={i.id} className="pane-line">
                        <Thumb
                          name={i.name}
                          imageKey={imageOf(i.productId)}
                          className="pane-thumb"
                        />

                        <div className="pane-line-main">
                          <button type="button" className="line-note" onClick={() => setNoting(i)}>
                            <b>
                              {i.name}
                              {i.optionLabel && <em> · {i.optionLabel}</em>}
                            </b>
                            {i.note ? (
                              <small className="line-note-text">{i.note}</small>
                            ) : (
                              <small className="line-note-add">+ ghi chú</small>
                            )}
                          </button>
                          <div className="stepper">
                            <button
                              type="button"
                              onClick={() => void bump(i, -1)}
                              aria-label={`Bớt ${i.name}`}
                            >
                              −
                            </button>
                            <span className="num">{qtyToText(i.qtyMilli)}</span>
                            <button
                              type="button"
                              onClick={() => void bump(i, 1)}
                              aria-label={`Thêm ${i.name}`}
                            >
                              +
                            </button>
                          </div>
                        </div>

                        <span className="pane-line-total num">{money(i.lineTotal)}</span>

                        <button
                          type="button"
                          className="pane-drop"
                          onClick={() => void drop(i.id)}
                          aria-label={`Bỏ ${i.name} khỏi đơn`}
                          title="Bỏ món"
                        >
                          🗑
                        </button>
                      </div>
                    ))}
                  </div>

                  <div className="pane-total">
                    <span>Tổng tiền</span>
                    <b className="num">{money(order.total)}đ</b>
                  </div>

                  {order.status === 'Paid' ? (
                    /*
                     * Đơn đã trả tiền: hai nút của việc phục vụ, không phải của việc bán.
                     * Quán thu tiền ngay lúc gọi món, nên đây mới là lúc dài nhất của một
                     * lượt khách — và là lúc người bán cần biết ly nào xong, bàn nào về.
                     */
                    <div className="pane-actions">
                      <button
                        type="button"
                        className={`btn ${order.readyAt ? 'btn-ghost' : 'btn-amber'}`}
                        onClick={() => void markReady(order.id, !order.readyAt)}
                      >
                        {order.readyAt ? 'Chưa xong' : 'Báo xong'}
                      </button>
                      <button
                        type="button"
                        className="btn btn-brand grow"
                        onClick={() => setClearing(true)}
                      >
                        Dọn bàn
                      </button>
                    </div>
                  ) : (
                    <div className="pane-actions">
                      <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => nav(`/order/${order.id}`)}
                      >
                        + Gọi thêm
                      </button>
                      <button
                        type="button"
                        className="btn btn-brand grow"
                        onClick={() => nav(`/pay/${order.id}`)}
                      >
                        Thanh toán <span aria-hidden="true">›</span>
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <>
                  {order ? (
                    /* Bàn đã mở nhưng chưa gọi món. Khoang bên phải phải nói đúng cái
                       đang diễn ra, không thể ghi "bàn đang trống" trong khi ô bàn bên
                       trái đeo nhãn có người ngồi. */
                    <div className="pane-empty">
                      <TableIcon icon={picked.icon} className="empty-icon" />
                      <p>
                        Bàn đã mở nhưng chưa gọi món nào.
                        <br />
                        <small>Ngồi {elapsedText(order.openedAt, now)}</small>
                      </p>
                    </div>
                  ) : picked.reservedAt ? (
                    <div className="pane-booked">
                      <span className="booked-tag">
                        <span aria-hidden="true">⏰</span> Đã đặt trước
                      </span>
                      <b className="booked-clock num">{clock(picked.reservedAt)}</b>
                      <span className="booked-day">{dayText(picked.reservedAt, new Date(now))}</span>

                      <div className="booked-who">
                        <b>{picked.reservedName ?? 'Khách đặt'}</b>
                        {/* Chỉ bốn số cuối: màn hình này treo ở quầy, ai đi ngang cũng đọc được. */}
                        {picked.reservedPhone && (
                          <span className="num">{maskPhone(picked.reservedPhone)}</span>
                        )}
                        {picked.reservedPartySize > 0 && (
                          <span>{picked.reservedPartySize} người</span>
                        )}
                      </div>

                      {picked.reservedNote && <p className="booked-note">{picked.reservedNote}</p>}
                    </div>
                  ) : (
                    <div className="pane-empty">
                      <TableIcon icon={picked.icon} className="empty-icon" />
                      <p>Bàn đang trống.</p>
                    </div>
                  )}

                  <div className="pane-actions">
                    {order ? (
                      <button
                        type="button"
                        className="btn btn-danger-ghost"
                        onClick={() => void clearTable(picked, order)}
                      >
                        Trả bàn về trống
                      </button>
                    ) : (
                      <button type="button" className="btn btn-ghost" onClick={() => onReserve(picked)}>
                        ⏰ Đặt trước
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn btn-brand grow"
                      onClick={() => void openOrder(picked)}
                    >
                      Gọi món <span aria-hidden="true">›</span>
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </aside>
      </div>

      {clearing && order && picked && (
        <Sheet title={`Dọn ${picked.name}?`} onClose={() => setClearing(false)}>
          <p className="sheet-note">
            Khách đã đứng dậy chưa? Dọn rồi thì bàn trắng lại và đơn {callNumber(order.code)} ·{' '}
            {money(order.total)}đ rời khỏi sơ đồ, không kéo về được nữa.
          </p>

          <div className="sheet-actions">
            <button type="button" className="btn btn-ghost" onClick={() => setClearing(false)}>
              Chưa, để đó
            </button>
            <button
              type="button"
              className="btn btn-brand grow"
              onClick={() => void finish(order, picked)}
            >
              Dọn bàn
            </button>
          </div>
        </Sheet>
      )}

      {noting && order && (
        <NoteSheet
          item={noting}
          onClose={() => setNoting(null)}
          onSave={async (note) => {
            await saveItems(
              order.id,
              order.items.map((x) => (x.id === noting.id ? { ...x, note } : x)),
            )
          }}
        />
      )}
    </div>
  )
}
